import React, { useState, useEffect, useRef } from 'react';
import HTMLFlipBook from 'react-pageflip';
import * as pdfjs from 'pdfjs-dist';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import FullscreenViewer from 'components/FullscreenViewer';
import { streamFile } from './function/api';
import Layout from 'Layout';
import { Helmet } from 'react-helmet-async';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const Page = React.forwardRef<HTMLDivElement, { src: string; alt: string }>(
    ({ src, alt }, ref) => (
        <div ref={ref} style={{ width: '100%', height: '100%', overflow: 'hidden', background: '#fff' }}>
            <img src={src} alt={alt} draggable={false} style={{ width: '100%', height: '100%', display: 'block', objectFit: 'fill' }} />
        </div>
    )
);
Page.displayName = 'Page';

function FlipbookViewer({ pages, onFlip, current, total }: {
    pages: string[];
    onFlip: (index: number) => void;
    current: number;
    total: number;
}) {
    const bookRef = useRef<any>(null);
    const sceneRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const scene = sceneRef.current;
        if (!scene) return;
        const parent = scene.querySelector<HTMLElement>('.stf__parent');
        if (parent) parent.style.minWidth = '0';
    });

    const flipPrev = () => bookRef.current?.pageFlip()?.flipPrev();
    const flipNext = () => bookRef.current?.pageFlip()?.flipNext();

    return (
        <div className="thirteen-weeks__hb-scene" ref={sceneRef}>
            {/* @ts-ignore */}
            <HTMLFlipBook
                ref={bookRef}
                width={550}
                height={778}
                size="stretch"
                minWidth={360}
                maxWidth={1200}
                minHeight={150}
                maxHeight={750}
                usePortrait={true}
                showCover={false}
                autoSize={true}
                maxShadowOpacity={0.5}
                showPageCorners={true}
                mobileScrollSupport={false}
                onFlip={(e: any) => onFlip(e.data)}
                className="thirteen-weeks__hb-flipbook"
            >
                {pages.map((src, i) => (
                    <Page key={i} src={src} alt={`p${i + 1}`} />
                ))}
            </HTMLFlipBook>

            <div className="thirteen-weeks__hb-nav">
                <button
                    className="thirteen-weeks__hb-nav-btn"
                    onClick={flipPrev}
                    disabled={current <= 0}
                    aria-label="Trang trước"
                >
                    <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M10 3L5 8l5 5" />
                    </svg>
                    <span>Trở lại</span>
                </button>
                <button
                    className="thirteen-weeks__hb-nav-btn"
                    onClick={flipNext}
                    disabled={current >= total - 1}
                    aria-label="Trang sau"
                >
                    <span>Qua trang</span>
                    <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M6 3l5 5-5 5" />
                    </svg>
                </button>
            </div>
        </div>
    );
}

const fetchPdfBytesViaStream = (fileId: string): Promise<Uint8Array> => {
    return new Promise((resolve, reject) => {
        const chunks: Uint8Array[] = [];
        streamFile(
            fileId,
            (chunk: Uint8Array) => {
                chunks.push(chunk);
            },
            () => {
                let totalLength = 0;
                for (const chunk of chunks) {
                    totalLength += chunk.length;
                }
                const fullBytes = new Uint8Array(totalLength);
                let offset = 0;
                for (const chunk of chunks) {
                    fullBytes.set(chunk, offset);
                    offset += chunk.length;
                }
                resolve(fullBytes);
            },
            (error: any) => {
                reject(error);
            }
        );
    });
};

export default function LibraryPdfViewer() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [pages, setPages] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [current, setCurrent] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);

    useEffect(() => {
        if (!id) {
            setError('ID tài liệu không hợp lệ');
            setLoading(false);
            return;
        }

        let cancelled = false;
        setPages([]);
        setLoading(true);
        setError(null);

        (async () => {
            try {
                const bytes = await fetchPdfBytesViaStream(id);
                const pdf = await pdfjs.getDocument({ data: bytes }).promise;
                const imgs: string[] = [];

                for (let i = 1; i <= pdf.numPages; i++) {
                    const pg = await pdf.getPage(i);
                    const vp = pg.getViewport({ scale: 2 });
                    const canvas = document.createElement('canvas');
                    canvas.width = vp.width;
                    canvas.height = vp.height;
                    await pg.render({ canvasContext: canvas.getContext('2d')!, viewport: vp, canvas }).promise;
                    imgs.push(canvas.toDataURL());
                }

                if (!cancelled) setPages(imgs);
            } catch (err) {
                if (!cancelled) setError(err instanceof Error ? err.message : 'Lỗi tải tài liệu');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [id]);

    const total = pages.length;
    const displayL = current + 1;
    const displayR = Math.min(current + 2, total);

    const pageInfo = !loading && !error ? (
        <span className="thirteen-weeks__hb-page-info">
            {displayL}–{displayR} / {total}
        </span>
    ) : null;

    return (
        <Layout>
            <Helmet>
                <title>PDF Viewer</title>
            </Helmet>
            <div className="thirteen-weeks__hb" style={{ minHeight: '100vh', padding: '20px' }}>
                <div className="thirteen-weeks__hb-header">
                    <button className="thirteen-weeks__hb-back" onClick={() => navigate(-1)}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M10 3L5 8l5 5" />
                        </svg>
                        Quay lại
                    </button>
                    <div className="thirteen-weeks__hb-header-right">
                        {pageInfo}
                        {!loading && !error && (
                            <div className="thirteen-weeks__hb-fullscreen">
                                <span className="thirteen-weeks__hb-fullscreen-label">Toàn màn hình</span>
                                <button
                                    className="thirteen-weeks__hb-fullscreen-btn"
                                    onClick={() => setIsFullscreen(true)}
                                    title="Mở toàn màn hình"
                                >
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M1 5V1h4M11 1h4v4M15 11v4h-4M5 15H1v-4" />
                                    </svg>
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {loading ? (
                    <div className="thirteen-weeks__hb-loading">
                        <div className="thirteen-weeks__hb-spinner" />
                        <span>Đang tải tài liệu PDF...</span>
                    </div>
                ) : error ? (
                    <div className="thirteen-weeks__hb-error">
                        <span>{error || t('handbookError')}</span>
                    </div>
                ) : (
                    <div className="flex justify-center items-center">
                        <FlipbookViewer pages={pages} onFlip={setCurrent} current={current} total={total} />
                    </div>
                )}

                <FullscreenViewer
                    open={isFullscreen}
                    onClose={() => setIsFullscreen(false)}
                    aspectRatio={(550 * 2) / 778}
                    headerLeft={pageInfo}
                >
                    <FlipbookViewer pages={pages} onFlip={setCurrent} current={current} total={total} />
                </FullscreenViewer>
            </div>
        </Layout>
    );
}
